/**
 * Laabha — typed client for the AIAccountant agent surface.
 *
 * Two import paths:
 *   1. FAST PATH  — runIngest() → POST /ingest (deterministic 3-tier parser, no Claude)
 *                   then confirmIngest() → POST /ingest-confirm (push XML to Tally)
 *                   Falls back to runAgent() on UNSUPPORTED_TYPE / parse error.
 *
 *   2. AGENT PATH — runAgent() → POST /agent (Claude orchestrates, costs credits)
 *                   Used as fallback for unknown bank formats / complex queries.
 */

import type { Envelope } from '@/types/chat';
import { authHeader } from './auth';

class ApiError extends Error {
  constructor(public code: string, message: string, public status: number) {
    super(message);
    this.name = 'ApiError';
  }
}

// ── Response interfaces ───────────────────────────────────────────────────────

interface AgentResult {
  response: string;
  turns: number;
  tool_calls: Array<{
    turn: number;
    tool: string;
    input_summary?: Record<string, string>;
    result?: string;
  }>;
  session_id: string;
  file_processed?: string;
  usage?: {
    input_tokens?: number;
    output_tokens?: number;
    cache_read_tokens?: number;
    cache_write_tokens?: number;
    cost_usd_raw?: number;
    cost_inr_raw?: number;
    cost_inr_charged?: number;
    balance_after?: number;
    fx_rate?: number;
    markup?: number;
    models_used?: string[];
  };
}

export interface IngestResult {
  session_id:   string;
  tier_used:    number;        // 1=deterministic, 2=generic text, 3=OCR
  detected_bank: string;
  count:        number;        // total transactions in XML
  auto:         number;        // auto-mapped (rule + AI confident)
  review:       number;        // medium-confidence, needs review
  suspense:     number;        // unmapped → Suspense bucket
  debit_total:  number;
  credit_total: number;
  drawings:     number;        // UPI drawings auto-assigned
  upi_mapped:   number;        // matched existing UPI ledgers
  upi_created:  number;        // new UPI ledger names created
  review_queue: Array<{ narration: string; amount: number; suggested_ledger: string }>;
  elapsed_sec:  number;
  user_message: string;        // Tally's XML response (empty if tally_offline)
  tally_offline: boolean;      // true = Tally unreachable, XML saved in session
  tally_url:    string;
  inferred_rules: Array<{ narration_pattern: string; ledger: string }>;
  inferred_rules_count: number;
  history_vouchers_scanned: number;
}

export interface ConfirmResult {
  success: boolean;
  created?: number;
  errors?: number;
  message?: string;
  user_message?: string; // gate-blocked reason from backend (prefer over message)
  blocked?: boolean;     // true when our quality gate rejected the import
}

interface FinancialReportResult {
  response: string;
  report_url?: string;
  fy_from: string;
  fy_to: string;
  session_id: string;
}

interface TallyPingResult {
  online: boolean;
  ms?: number;
  tally_url?: string;
  error?: string;
}

// ── Shared helpers ────────────────────────────────────────────────────────────

async function unwrap<T>(resp: Response): Promise<T> {
  if (resp.status === 401) {
    throw new ApiError('AUTH_REQUIRED', 'Authorization failed for this request.', 401);
  }
  if (resp.status === 402 || resp.status === 403) {
    let body: Envelope<T> | null = null;
    try { body = (await resp.clone().json()) as Envelope<T>; } catch {}
    const code = body?.error?.code ?? `HTTP_${resp.status}`;
    const msg = body?.error?.message ?? 'Insufficient balance — recharge to continue.';
    if (isBillingDepletedError(code, msg)) {
      window.dispatchEvent(
        new CustomEvent('axondesktop.billing.depleted', { detail: { code, msg } }),
      );
      throw new ApiError('BALANCE_DEPLETED', msg, resp.status);
    }
    throw new ApiError(code, msg, resp.status);
  }
  if (!resp.ok && resp.status !== 422) {
    let txt = '';
    try { txt = await resp.text(); } catch {}
    throw new ApiError(`HTTP_${resp.status}`, txt.slice(0, 200) || resp.statusText, resp.status);
  }
  const body = (await resp.json()) as Envelope<T>;
  if (!body.success) {
    const code = body.error?.code ?? 'UNKNOWN';
    const msg = body.error?.message ?? 'Failed';
    if (isBillingDepletedError(code, msg)) {
      window.dispatchEvent(
        new CustomEvent('axondesktop.billing.depleted', { detail: { code, msg } }),
      );
      throw new ApiError('BALANCE_DEPLETED', msg, resp.status);
    }
    throw new ApiError(code, msg, resp.status);
  }
  if (body.data === null) throw new ApiError('EMPTY_DATA', 'Envelope data null', resp.status);
  return body.data;
}

function isBillingDepletedError(code: string, msg: string): boolean {
  const c = code.toUpperCase();
  const m = msg.toLowerCase();
  return (
    c === 'BALANCE_DEPLETED' ||
    c === 'INSUFFICIENT_BALANCE' ||
    c === 'INSUFFICIENT_CREDITS' ||
    c === 'BILLING_EXHAUSTED' ||
    c === 'PAYMENT_REQUIRED' ||
    m.includes('insufficient') ||
    m.includes('depleted') ||
    m.includes('recharge')
  );
}

// ── Bank detection helpers ───────────────

const BANK_FILENAME_MAP: Record<string, string> = {
  hdfc: 'hdfc', sbi: 'sbi', icici: 'icici', axis: 'axis', kotak: 'kotak',
  canara: 'canara', canarabank: 'canara', syndicatebank: 'canara', synd: 'canara',
  cub: 'cub', 'city-union': 'cub', cityunion: 'cub',
  pnb: 'pnb', punjab: 'pnb', obc: 'pnb', oriental: 'pnb',
  bob: 'bob', baroda: 'bob', dena: 'bob', vijaya: 'bob',
  union: 'union', ubi: 'union',
  iob: 'iob', 'indian-overseas': 'iob',
  idbi: 'idbi', boi: 'boi', indusind: 'indusind', yes: 'yesbank',
  rbl: 'rbl', federal: 'federal', karnataka: 'karnataka',
};

/** Detect bank from filename (e.g. "hdfc_march.pdf" → "hdfc"). */
export function detectBankFromFilename(filename: string): string {
  const base = filename.toLowerCase().replace(/[^a-z0-9]/g, '');
  for (const [key, bankId] of Object.entries(BANK_FILENAME_MAP)) {
    if (base.includes(key)) return bankId;
  }
  return '';
}

const BANK_MSG_MAP: Array<[RegExp, string]> = [
  [/\bhdfc\b/i, 'hdfc'], [/\bsbi\b|\bstate bank\b/i, 'sbi'],
  [/\bicici\b/i, 'icici'], [/\baxis\s*bank\b/i, 'axis'],
  [/\bkotak\b/i, 'kotak'], [/\bcanara\b|\bsynd(icate)?\b/i, 'canara'],
  [/\bcub\b|\bcity.?union\b/i, 'cub'], [/\bpnb\b|\bpunjab.*national\b/i, 'pnb'],
  [/\bbob\b|\bbank.*baroda\b/i, 'bob'], [/\bunion.*bank\b|\bubi\b/i, 'union'],
  [/\biob\b|\bindian.*overseas\b/i, 'iob'], [/\bidbi\b/i, 'idbi'],
  [/\bboi\b|\bbank.*india\b/i, 'boi'], [/\bindusind\b/i, 'indusind'],
  [/\byes\s*bank\b/i, 'yesbank'], [/\brbl\b/i, 'rbl'],
  [/\bfederal\s*bank\b/i, 'federal'],
];

/** Detect bank from message text typed by user. */
export function detectBankFromMessage(msg: string): string {
  for (const [re, bankId] of BANK_MSG_MAP) {
    if (re.test(msg)) return bankId;
  }
  return '';
}

/** Extract an ngrok URL from user message text (e.g. "import at https://abc.ngrok-free.app"). */
export function extractNgrokUrl(text: string): string {
  const m = text.match(/https?:\/\/[a-z0-9-]+\.ngrok(?:-free)?\.(?:app|dev)[^\s]*/i)
         ?? text.match(/https?:\/\/[a-z0-9-]+\.ngrok\.io[^\s]*/i);
  return m ? m[0].replace(/[.,;]+$/, '') : '';
}

// ── Fast import path: /ingest → /ingest-confirm ───────────────────────────────

/**
 * Call the deterministic 3-tier ingest pipeline directly — no Claude needed
 * for known bank formats. Falls back gracefully when the caller catches
 * ApiError with code UNSUPPORTED_TYPE or any parse failure.
 */
export async function runIngest(opts: {
  file: File;
  tallyUrl?: string;
  companyId?: string;
  bankLedger?: string;
  bankId?: string;
  progressId?: string;
}): Promise<IngestResult> {
  const fd = new FormData();
  fd.append('file', opts.file);
  if (opts.tallyUrl)   fd.append('tally_url',   opts.tallyUrl);
  if (opts.companyId)  fd.append('company_id',  opts.companyId);
  if (opts.bankLedger) fd.append('bank_ledger', opts.bankLedger);
  if (opts.bankId)     fd.append('bank_id',     opts.bankId);
  if (opts.progressId) fd.append('progress_id', opts.progressId);

  // No client-side timeout here: the BLL holds the connection open while a
  // Celery worker processes the file (queue wait + OCR can exceed 20 min for
  // large PDFs). The server enforces a 1-hour ceiling. Progress events keep
  // the UI informed via the separate progress poll.
  const resp = await fetch('/api/v1/aiaccountant/ingest', {
    method: 'POST',
    credentials: 'include',
    headers: authHeader(),
    body: fd,
  });
  return unwrap<IngestResult>(resp);
}

/**
 * Push a previously-ingested bank statement (stored server-side by session_id)
 * into TallyPrime. Call this after /ingest when Tally was offline.
 */
export async function confirmIngest(opts: {
  sessionId: string;
  tallyUrl: string;
}): Promise<ConfirmResult> {
  const resp = await fetch('/api/v1/aiaccountant/ingest-confirm', {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', ...authHeader() },
    body: JSON.stringify({ session_id: opts.sessionId, tally_url: opts.tallyUrl }),
  });
  return unwrap<ConfirmResult>(resp);
}

// ── Agent path (Claude-orchestrated) ─────────────────────────────────────────

/** Run the AIAccountant agent — multipart so PDFs/Excel can be attached. */
export async function runAgent(opts: {
  message: string;
  file?: File;
  tallyUrl?: string;
  companyId?: string;
  bankLedger?: string;
  bankId?: string;
  sessionId?: string;
  model?: string;
  progressId?: string;
}): Promise<AgentResult> {
  const fd = new FormData();
  fd.append('message', opts.message);
  if (opts.file)       fd.append('file',        opts.file);
  if (opts.tallyUrl)   fd.append('tally_url',   opts.tallyUrl);
  if (opts.companyId)  fd.append('company_id',  opts.companyId);
  if (opts.bankLedger) fd.append('bank_ledger', opts.bankLedger);
  if (opts.bankId)     fd.append('bank_id',     opts.bankId);
  if (opts.sessionId)  fd.append('session_id',  opts.sessionId);
  if (opts.model)      fd.append('model',       opts.model);
  if (opts.progressId) fd.append('progress_id', opts.progressId);

  const resp = await fetch('/api/v1/aiaccountant/agent', {
    method: 'POST',
    credentials: 'include',
    headers: authHeader(),
    body: fd,
  });
  return unwrap<AgentResult>(resp);
}

/** Run the Financial Report agent — multipart body. */
export async function runFinancialReport(opts: {
  message: string;
  tallyUrl?: string;
  companyId?: string;
  sessionId?: string;
  fyFrom?: string;
  fyTo?: string;
  model?: string;
}): Promise<FinancialReportResult> {
  const fd = new FormData();
  fd.append('message', opts.message);
  if (opts.tallyUrl)  fd.append('tally_url',  opts.tallyUrl);
  if (opts.companyId) fd.append('company_id', opts.companyId);
  if (opts.sessionId) fd.append('session_id', opts.sessionId);
  if (opts.fyFrom)    fd.append('fy_from',    opts.fyFrom);
  if (opts.fyTo)      fd.append('fy_to',      opts.fyTo);
  if (opts.model)     fd.append('model',      opts.model);

  const resp = await fetch('/api/v1/aiaccountant/agent-financial-report', {
    method: 'POST',
    credentials: 'include',
    headers: authHeader(),
    body: fd,
  });
  return unwrap<FinancialReportResult>(resp);
}

// ── Tally connection ──────────────────────────────────────────────────────────

/**
 * Ping Tally via the gateway BLL. Returns online status + round-trip time.
 * NEVER throws — every failure is normalised to `{ online: false }`.
 */
export async function pingTally(tallyUrl: string): Promise<TallyPingResult> {
  if (!tallyUrl) return { online: false, error: 'No tally_url configured' };
  let resp: Response;
  try {
    resp = await fetch('/api/v1/aiaccountant/tally-ping', {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json', ...authHeader() },
      body: JSON.stringify({ tally_url: tallyUrl }),
    });
  } catch {
    return { online: false, error: 'network' };
  }
  if (resp.status === 401 || resp.status === 404) {
    return { online: false, error: 'ping route unavailable' };
  }
  try {
    const body = (await resp.json()) as Envelope<TallyPingResult>;
    if (body.data) return body.data;
    return { online: false, error: body.error?.message ?? 'no data' };
  } catch {
    return { online: false, error: 'invalid response' };
  }
}

export interface TallyCompaniesResult {
  companies: string[];
  count: number;
  // Present only when the request included `?q=...`
  query?: string;
  best_match?: string;
  best_score?: number;
  exact_match?: boolean;
  suggest?: boolean;       // true when there's a close-but-not-exact match
}

/**
 * List all companies currently in TallyPrime. Optionally pass `query` to also
 * get the closest fuzzy match — used by Settings UI to show "Did you mean?"
 * when the typed Company name has a typo.
 */
export async function listTallyCompanies(
  tallyUrl: string,
  query?: string,
): Promise<TallyCompaniesResult> {
  const params = new URLSearchParams({ tally_url: tallyUrl });
  if (query) params.set('q', query);
  const resp = await fetch(
    `/api/v1/aiaccountant/tally-companies?${params.toString()}`,
    { credentials: 'include', headers: authHeader() },
  );
  const body = await resp.json();
  if (!body.success) throw new Error(body.error?.message ?? 'Could not list companies');
  return body.data;
}

export interface TallyBankLedgersResult {
  ledgers: string[];
  count: number;
  // Present only when the request included `?q=...`
  query?: string;
  best_match?: string;
  best_score?: number;
  exact_match?: boolean;
  suggest?: boolean;
}

/**
 * List bank-account ledgers currently in TallyPrime (Bank Accounts / OD / OCC).
 * Optionally pass `query` to also get the closest fuzzy match — used by the
 * Bank Ledger field in Settings to show "Did you mean?" when typed value is
 * shorter than the real ledger name (common pattern: user types "Bank of Baroda"
 * but Tally has "Bank of Baroda (74030100002717)").
 */
export async function listTallyBankLedgers(
  tallyUrl: string,
  query?: string,
): Promise<TallyBankLedgersResult> {
  const params = new URLSearchParams({ tally_url: tallyUrl });
  if (query) params.set('q', query);
  const resp = await fetch(
    `/api/v1/aiaccountant/tally-bank-ledgers?${params.toString()}`,
    { credentials: 'include', headers: authHeader() },
  );
  const body = await resp.json();
  if (!body.success) throw new Error(body.error?.message ?? 'Could not list bank ledgers');
  return body.data;
}

/**
 * Ping Tally and return the name of the company currently open in TallyPrime.
 * Used to auto-fill the Active Company field in Settings.
 */
export async function detectTallyCompany(
  tallyUrl: string,
): Promise<{ company_id: string | null; company_name: string; matched: boolean }> {
  const resp = await fetch(
    `/api/v1/aiaccountant/detect-company?tally_url=${encodeURIComponent(tallyUrl)}`,
    { credentials: 'include', headers: authHeader() },
  );
  const body = await resp.json();
  if (!body.success) throw new Error(body.error?.message ?? 'Detect failed');
  return body.data;
}

// ── Mapping rules ─────────────────────────────────────────────────────────────

export interface MappingRule {
  contains:  string;
  ledger:    string;
  txn_type:  'Both' | 'Debit' | 'Credit';
  notes:     string;
  enabled:   boolean;
}

function parseRuleRow(r: Record<string, string>): MappingRule {
  return {
    contains:  r['If Narration Contains'] ?? '',
    ledger:    r['Ledger Name']           ?? '',
    txn_type: (['Both', 'Debit', 'Credit'].includes(r['Transaction Type'])
      ? r['Transaction Type']
      : 'Both') as MappingRule['txn_type'],
    notes:   r['Notes']   ?? '',
    enabled: String(r['Enabled'] ?? 'true').toLowerCase() !== 'false',
  };
}

/** Fetch company-specific mapping rules. */
export async function getMappingRules(companyId: string): Promise<MappingRule[]> {
  const resp = await fetch(
    `/api/v1/aiaccountant/companies/${encodeURIComponent(companyId)}`,
    { credentials: 'include', headers: authHeader() },
  );
  const body = await resp.json();
  if (!body.success) throw new Error(body.error?.message ?? 'Failed to load rules');
  return (body.data?.mapping_rules ?? []).map(parseRuleRow);
}

/** Overwrite company-specific mapping rules (order = priority). */
export async function saveMappingRules(companyId: string, rules: MappingRule[]): Promise<void> {
  const resp = await fetch(
    `/api/v1/aiaccountant/companies/${encodeURIComponent(companyId)}/mapping-rules`,
    {
      method: 'PUT',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json', ...authHeader() },
      body: JSON.stringify({ rules }),
    },
  );
  const body = await resp.json();
  if (!body.success) throw new Error(body.error?.message ?? 'Save failed');
}

/** Fetch global mapping rules (apply to ALL companies, checked first). */
export async function getGlobalRules(): Promise<MappingRule[]> {
  const resp = await fetch('/api/v1/aiaccountant/global-rules', {
    credentials: 'include',
    headers: authHeader(),
  });
  const body = await resp.json();
  if (!body.success) throw new Error(body.error?.message ?? 'Failed to load global rules');
  return (body.data?.mapping_rules ?? []).map(parseRuleRow);
}

/** Overwrite global mapping rules. */
export async function saveGlobalRules(rules: MappingRule[]): Promise<void> {
  const resp = await fetch('/api/v1/aiaccountant/global-rules', {
    method: 'PUT',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', ...authHeader() },
    body: JSON.stringify({ rules }),
  });
  const body = await resp.json();
  if (!body.success) throw new Error(body.error?.message ?? 'Save failed');
}

/**
 * Append D.1 historically-inferred rules, de-duped by (pattern, ledger).
 * New rules go to END (lowest priority, so explicit rules keep winning).
 */
export interface InferredRulePayload {
  narration_pattern: string;
  ledger: string;
}

export async function appendInferredRules(
  companyId: string,
  inferred: InferredRulePayload[],
): Promise<number> {
  if (!companyId || !inferred.length) return 0;
  const existing = await getMappingRules(companyId);
  const existingKeys = new Set(
    existing.map((r) => `${r.contains.toLowerCase().trim()}|${r.ledger.toLowerCase().trim()}`),
  );
  const toAppend: MappingRule[] = [];
  for (const inf of inferred) {
    const key = `${inf.narration_pattern.toLowerCase().trim()}|${inf.ledger.toLowerCase().trim()}`;
    if (existingKeys.has(key)) continue;
    existingKeys.add(key);
    toAppend.push({
      contains: inf.narration_pattern, ledger: inf.ledger,
      txn_type: 'Both', notes: 'Auto-inferred from Tally history (last 3 months)',
      enabled: true,
    });
  }
  if (!toAppend.length) return 0;
  await saveMappingRules(companyId, [...existing, ...toAppend]);
  return toAppend.length;
}

/** Generate a client-side progress_id matching handler.js's format. */
export function makeProgressId(): string {
  const r = () => Math.random().toString(16).slice(2).padEnd(8, '0').slice(0, 8);
  return `pid-${Date.now().toString(16)}-${r()}-${r()}`;
}

export { ApiError };

// ── Financial Report via tally-chat-bll (Anthropic Agents SDK) ───────────────
// New path: UI → /tally-chat-bll → claude-agent-sdk → Tally MCP (port 8275)
// Bearer token is a service-level API key (dev only).

const _FR_BLL_BASE  = '/tally-chat-bll';
const _FR_BLL_TOKEN = 'e1d68d4530550dce2e22ead801ba889633cbe27c3967d9bc1ebf40e4d5412b7c';
const _FR_MCP_URL   = 'http://172.25.0.23:8275/mcp';

function _frHeaders(tenantId: string): Record<string, string> {
  return {
    Authorization: `Bearer ${_FR_BLL_TOKEN}`,
    'X-Tenant-Id':  tenantId || 'default',
  };
}

function _frSystemPrompt(tallyUrl: string, companyId: string, fyFrom: string, fyTo: string): string {
  const ctx = [
    tallyUrl   && `TallyPrime URL: ${tallyUrl}`,
    companyId  && `Company: ${companyId}`,
    fyFrom     && `FY from: ${fyFrom}`,
    fyTo       && `FY to: ${fyTo}`,
  ].filter(Boolean).join('\n');
  return `You are the Laabha Financial Report assistant for AIAccountant. \
Generate a complete ICAI-compliant financial report from TallyPrime data.

${ctx}

Steps:
1. tally_ping — verify TallyPrime is reachable, confirm company name
2. tally_extract_financials — fetch trial balance for the fiscal year above
3. Classify ledgers per Schedule III, Companies Act 2013 (ICAI Guidance Note):
   - Balance Sheet: Fixed Assets, Current Assets, Equity, Long-term Liabilities, Current Liabilities
   - P&L: Revenue from Operations, Other Income, Cost of Materials, Employee Benefits, Finance Costs, Other Expenses
4. generate_financial_report — pass the fully-classified JSON structure
5. Present the download URL and key financial highlights to the user.

Use the exact canonical company name from step 1 in all subsequent calls.`;
}

/** Create a tally-chat-bll conversation scoped to the FR agent. Returns conv id. */
export async function createFrConversation(opts: {
  tenantId:  string;
  tallyUrl:  string;
  companyId: string;
  fyFrom:    string;
  fyTo:      string;
}): Promise<{ id: string }> {
  const resp = await fetch(`${_FR_BLL_BASE}/v1/conversations`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ..._frHeaders(opts.tenantId) },
    body: JSON.stringify({
      title:         'Financial Report',
      system_prompt: _frSystemPrompt(opts.tallyUrl, opts.companyId, opts.fyFrom, opts.fyTo),
      mcp_servers:   { tally: { type: 'http', url: _FR_MCP_URL } },
      max_turns:     30,
    }),
  });
  if (!resp.ok) {
    const txt = await resp.text().catch(() => '');
    throw new ApiError('FR_CONV_CREATE_FAILED', `${resp.status} ${txt.slice(0, 200)}`, resp.status);
  }
  const body = await resp.json() as { data?: { id: string } };
  const id = body?.data?.id;
  if (!id) throw new ApiError('FR_CONV_NO_ID', 'createFrConversation: no id in response', 500);
  return { id };
}

export interface FrDoneResult {
  response:    string;
  tool_calls?: Array<{ tool: string; result?: string; [k: string]: unknown }>;
  usage?:      Record<string, unknown>;
}

export interface FrStreamCallbacks {
  onTextDelta: (text: string)                     => void;
  onToolUse:   (tool: string, step: number)       => void;
  onToolDone:  (tool: string, step: number)       => void;
  onDone:      (result: FrDoneResult)             => void;
  onError:     (msg: string)                      => void;
}

/** Stream a message against an existing FR conversation. Returns a cancel handle. */
export function streamFrConversation(
  convId:   string,
  prompt:   string,
  tenantId: string,
  cb:       FrStreamCallbacks,
): { cancel: () => void } {
  const ctrl = new AbortController();

  (async () => {
    const fd = new FormData();
    fd.append('prompt', prompt);

    let resp: Response;
    try {
      resp = await fetch(`${_FR_BLL_BASE}/v1/conversations/${convId}/stream`, {
        method:  'POST',
        headers: _frHeaders(tenantId),
        body:    fd,
        signal:  ctrl.signal,
      });
    } catch (err) {
      if ((err as Error)?.name !== 'AbortError') cb.onError((err as Error).message);
      return;
    }

    if (!resp.ok || !resp.body) {
      const txt = await resp.text().catch(() => '');
      cb.onError(`FR stream ${resp.status}: ${txt.slice(0, 200)}`);
      return;
    }

    const reader  = resp.body.getReader();
    const decoder = new TextDecoder();
    let buf     = '';
    let stepCtr = 0;

    try {
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });

        let boundary = buf.indexOf('\n\n');
        while (boundary !== -1) {
          const frame = buf.slice(0, boundary);
          buf         = buf.slice(boundary + 2);
          boundary    = buf.indexOf('\n\n');

          let evtName  = '';
          let dataLine = '';
          for (const line of frame.split('\n')) {
            if (line.startsWith('event:')) evtName  = line.slice(6).trim();
            else if (line.startsWith('data:'))  dataLine += line.slice(5).trim();
          }
          if (!dataLine) continue;

          let parsed: Record<string, unknown>;
          try { parsed = JSON.parse(dataLine) as Record<string, unknown>; }
          catch { continue; }

          const evtType = (parsed.type as string) ?? evtName;
          switch (evtType) {
            case 'text_delta':
              cb.onTextDelta((parsed.text as string) ?? '');
              break;
            case 'tool_use':
              stepCtr++;
              cb.onToolUse((parsed.tool as string) ?? evtType, stepCtr);
              break;
            case 'tool_result':
              cb.onToolDone((parsed.tool as string) ?? evtType, stepCtr);
              break;
            case 'done':
              cb.onDone({
                response:   (parsed.response as string) ?? (parsed.final_text as string) ?? '',
                tool_calls: parsed.tool_calls as FrDoneResult['tool_calls'],
                usage:      parsed.usage as Record<string, unknown> | undefined,
              });
              break;
            case 'error':
              cb.onError((parsed.message as string) ?? 'Unknown FR stream error');
              break;
          }
        }
      }
    } catch (err) {
      if ((err as Error)?.name !== 'AbortError') cb.onError((err as Error).message);
    }
  })();

  return { cancel: () => ctrl.abort() };
}
